import React, { useRef } from 'react';
import { Camera, Calendar, Coins } from 'lucide-react';
import { t, Language } from '../../i18n';

interface ProfileSectionProps {
    user: any;
    userPoints: number;
    lang: Language;
    onEditNickname: () => void;
    onAvatarChange: (file: File) => void;
    onBackgroundChange: (file: File) => void;
    favoriteTeam?: { name: string; logo: string } | null;
}

export const ProfileSection: React.FC<ProfileSectionProps> = React.memo(({ 
    user, 
    userPoints, 
    lang, 
    onEditNickname, 
    onAvatarChange, 
    onBackgroundChange,
    favoriteTeam
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const backgroundInputRef = useRef<HTMLInputElement>(null);

    const formatJoinedDate = (dateStr: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}.${month}.${day}`;
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'background') => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (type === 'avatar') onAvatarChange(file);
        else onBackgroundChange(file);
        e.target.value = '';
    };

    return (
        <div className="relative">
            <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*" 
                onChange={(e) => handleFileChange(e, 'avatar')} 
            />
            <input 
                type="file" 
                ref={backgroundInputRef} 
                className="hidden" 
                accept="image/*" 
                onChange={(e) => handleFileChange(e, 'background')} 
            />

            <div className="h-44 md:h-64 bg-slate-900 overflow-hidden relative group">
                <img 
                    src={user.backgroundUrl || "https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80&w=1200"} 
                    className="w-full h-full object-cover opacity-60 md:group-hover:opacity-50 transition-all duration-500" 
                    alt="Cover"
                />
                <button 
                    onClick={() => backgroundInputRef.current?.click()} 
                    className="absolute top-4 right-4 bg-black/40 backdrop-blur-md p-2 rounded-full border border-white/20 text-white md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                >
                    <Camera size={20} />
                </button>
            </div>
            
            <div className="px-6 relative flex justify-between items-end -mt-14 mb-6">
                <div className="relative">
                    <div className="w-28 h-28 md:w-36 md:h-36 rounded-full border-[5px] border-white bg-white overflow-hidden shadow-xl relative group">
                        <img 
                            src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=0f172a&color=fff&size=256`} 
                            className="w-full h-full object-cover" 
                            alt="Avatar"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                if (!target.src.includes('ui-avatars.com')) {
                                    target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=0f172a&color=fff&size=256`;
                                }
                            }}
                        />
                        <button 
                            onClick={() => fileInputRef.current?.click()} 
                            className="absolute inset-0 bg-black/40 text-white md:opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                            <Camera size={24} />
                        </button>
                    </div>
                    {favoriteTeam && (
                        <div className="absolute bottom-1 right-1 w-8 h-8 md:w-11 md:h-11 rounded-full bg-white p-1 md:p-1.5 shadow-lg border border-slate-100 flex items-center justify-center z-10 animate-in zoom-in duration-300" title={favoriteTeam.name}>
                            <img src={favoriteTeam.logo} alt={favoriteTeam.name} className="w-full h-full object-contain" />
                        </div>
                    )}
                </div>
                <div className="pb-2">
                    <button 
                        onClick={onEditNickname}
                        className="px-6 py-2 rounded-full border border-slate-200 text-sm font-black text-slate-900 md:hover:bg-slate-50 transition-all active:scale-95 bg-white shadow-sm"
                    >
                        {t(lang, 'edit')}
                    </button>
                </div>
            </div>

            <div className="px-6 mb-8">
                <div className="flex items-center gap-3 mb-1.5">
                    <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-none uppercase">{user.name}</h2>
                    </div>
                    <div className="bg-rose-50 text-rose-500 px-3 py-1 rounded-full flex items-center gap-1">
                        <Coins size={12} className="fill-rose-500" />
                        <span className="text-[10px] font-black tracking-tighter">{userPoints.toLocaleString()} P</span>
                    </div>
                </div>
            
                <div className="flex items-center gap-1.5 text-slate-500 text-[11px] font-black uppercase tracking-widest mt-4">
                    <Calendar size={14} className="text-slate-300" />
                    <span>{t(lang, 'joined')} {formatJoinedDate(user.joinDate)}</span>
                </div>
            </div>
        </div>
    );
});

ProfileSection.displayName = 'ProfileSection';
